/**
 * ops 式（docs/spec/complexity-meter.md §5.1）の手書き再帰下降パーサ。eval / Function は使わない。
 *
 * expr    := term (("+" | "-") term)*
 * term    := factor (("*" | "/") factor)*
 * factor  := unary ("^" factor)?          // ^ は右結合
 * unary   := "-" unary | primary
 * primary := number | ident | func "(" expr ")" | "(" expr ")"
 */

export type OpsNode =
  | { type: 'num'; value: number }
  | { type: 'var'; name: string }
  | { type: 'neg'; arg: OpsNode }
  | { type: 'bin'; op: '+' | '-' | '*' | '/' | '^'; left: OpsNode; right: OpsNode }
  | { type: 'call'; fn: 'log2' | 'sqrt'; arg: OpsNode };

export class OpsError extends Error {
  readonly position: number;
  constructor(message: string, position: number) {
    super(message);
    this.name = 'OpsError';
    this.position = position;
  }
}

type Token =
  | { k: 'num'; v: number; p: number }
  | { k: 'ident'; v: string; p: number }
  | { k: 'func'; v: 'log2' | 'sqrt'; p: number }
  | { k: 'op'; v: string; p: number }
  | { k: 'end'; p: number };

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    const ws = /^\s+/.exec(rest);
    if (ws) {
      i += ws[0].length;
      continue;
    }
    const num = /^[0-9]+(?:\.[0-9]+)?(?:e[0-9]+)?/.exec(rest);
    if (num) {
      out.push({ k: 'num', v: Number(num[0]), p: i });
      i += num[0].length;
      continue;
    }
    const ident = /^[A-Z][A-Za-z0-9]*/.exec(rest);
    if (ident) {
      out.push({ k: 'ident', v: ident[0], p: i });
      i += ident[0].length;
      continue;
    }
    const fn = /^(log2|sqrt)(?![A-Za-z0-9])/.exec(rest);
    if (fn && (fn[1] === 'log2' || fn[1] === 'sqrt')) {
      out.push({ k: 'func', v: fn[1], p: i });
      i += fn[0].length;
      continue;
    }
    const ch = src[i] ?? '';
    if ('+-*/^()'.includes(ch)) {
      out.push({ k: 'op', v: ch, p: i });
      i += 1;
      continue;
    }
    throw new OpsError(`unexpected character '${ch}'`, i);
  }
  out.push({ k: 'end', p: src.length });
  return out;
}

/** ops 式を構文解析する。構文エラーは {@link OpsError}。 */
export function parseOps(src: string): OpsNode {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = (): Token => tokens[pos] ?? { k: 'end', p: src.length };
  const isOp = (v: string): boolean => {
    const t = peek();
    return t.k === 'op' && t.v === v;
  };
  const expectOp = (v: string): void => {
    if (!isOp(v)) throw new OpsError(`expected '${v}'`, peek().p);
    pos += 1;
  };

  function expr(): OpsNode {
    let left = term();
    while (isOp('+') || isOp('-')) {
      const op = isOp('+') ? '+' : '-';
      pos += 1;
      left = { type: 'bin', op, left, right: term() };
    }
    return left;
  }
  function term(): OpsNode {
    let left = factor();
    while (isOp('*') || isOp('/')) {
      const op = isOp('*') ? '*' : '/';
      pos += 1;
      left = { type: 'bin', op, left, right: factor() };
    }
    return left;
  }
  function factor(): OpsNode {
    const base = unary();
    if (isOp('^')) {
      pos += 1;
      return { type: 'bin', op: '^', left: base, right: factor() };
    }
    return base;
  }
  function unary(): OpsNode {
    if (isOp('-')) {
      pos += 1;
      return { type: 'neg', arg: unary() };
    }
    return primary();
  }
  function primary(): OpsNode {
    const t = peek();
    switch (t.k) {
      case 'num':
        pos += 1;
        return { type: 'num', value: t.v };
      case 'ident':
        pos += 1;
        return { type: 'var', name: t.v };
      case 'func': {
        pos += 1;
        expectOp('(');
        const arg = expr();
        expectOp(')');
        return { type: 'call', fn: t.v, arg };
      }
      case 'op':
        if (t.v === '(') {
          pos += 1;
          const inner = expr();
          expectOp(')');
          return inner;
        }
        throw new OpsError(`unexpected '${t.v}'`, t.p);
      case 'end':
        throw new OpsError('unexpected end of expression', t.p);
    }
  }

  const tree = expr();
  const last = peek();
  if (last.k !== 'end') throw new OpsError('unexpected token', last.p);
  return tree;
}

/** 式に出てくる識別子（verify-cards が constraints と照合する。§5.1）。 */
export function opsIdentifiers(src: string): string[] {
  const names = new Set<string>();
  const walk = (n: OpsNode): void => {
    switch (n.type) {
      case 'var':
        names.add(n.name);
        break;
      case 'neg':
      case 'call':
        walk(n.arg);
        break;
      case 'bin':
        walk(n.left);
        walk(n.right);
        break;
      case 'num':
        break;
    }
  };
  walk(parseOps(src));
  return [...names];
}

function evalNode(n: OpsNode, vars: Readonly<Record<string, number>>): number {
  switch (n.type) {
    case 'num':
      return n.value;
    case 'var': {
      const v = vars[n.name];
      if (v === undefined) throw new OpsError(`unknown identifier '${n.name}'`, -1);
      return v;
    }
    case 'neg':
      return -evalNode(n.arg, vars);
    case 'call': {
      const x = evalNode(n.arg, vars);
      return n.fn === 'log2' ? Math.log2(x) : Math.sqrt(x);
    }
    case 'bin': {
      const a = evalNode(n.left, vars);
      const b = evalNode(n.right, vars);
      switch (n.op) {
        case '+':
          return a + b;
        case '-':
          return a - b;
        case '*':
          return a * b;
        case '/':
          return a / b;
        case '^':
          return a ** b;
      }
    }
  }
}

/** ops 式を倍精度で評価する（§5.1）。vars に無い識別子は {@link OpsError}。 */
export function evalOps(src: string | OpsNode, vars: Readonly<Record<string, number>>): number {
  return evalNode(typeof src === 'string' ? parseOps(src) : src, vars);
}
